import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  ieltsSpeakingApi,
  type SpeakingCriterionKey,
  type SpeakingCueCard,
  type SpeakingPart,
  type SpeakingSession,
  type SpeakingTurn,
  type TrainerHandoff,
} from '@/api/ielts';
import { playUrl } from '@/lib/wordAudio';
import { speakingApi } from '@/api/speaking';
import { LiveSpeakingSession } from '@/components/speaking/LiveSpeakingSession';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { humanizeError } from '@/lib/errors';

const TAB_IDS = ['practice', 'history'] as const;
type TabId = (typeof TAB_IDS)[number];

const CRITERIA: SpeakingCriterionKey[] = ['fc', 'lr', 'gra', 'pr'];

/** Part 2 preparation time, in seconds (the real IELTS allows ~1 minute). */
const PREP_SECONDS = 60;

/** Band → 0..1 fill for the criterion bars / overall ring (IELTS max is 9). */
function bandFraction(band?: number): number {
  if (typeof band !== 'number') return 0;
  return Math.max(0, Math.min(1, band / 9));
}

export function StudentIELTSSpeakingPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<TabId>('practice');

  const TABS: { id: TabId; label: string }[] = [
    { id: 'practice', label: t('ielts.speaking.tabPractice') },
    { id: 'history', label: t('ielts.speaking.tabHistory') },
  ];

  return (
    <>
      <PageHeader title={t('ielts.speaking.title')} subtitle={t('ielts.speaking.subtitle')} />
      <div className="mb-6 flex gap-2">
        {TABS.map((tb) => (
          <button
            key={tb.id}
            onClick={() => setTab(tb.id)}
            className={cn(
              'rounded-pill px-4 py-1.5 text-xs font-semibold uppercase tracking-label transition',
              tab === tb.id
                ? 'bg-primary text-on-primary'
                : 'bg-surface-container-lowest text-on-surface-variant ghost-border hover:bg-surface-container-low',
            )}
          >
            {tb.label}
          </button>
        ))}
      </div>

      {tab === 'practice' && <PracticeTab />}
      {tab === 'history' && <HistoryTab />}
    </>
  );
}

// ---------------------------------------------------------------------------
// Practice tab: start screen → live session → result card
// ---------------------------------------------------------------------------

type PracticeMode = 'live' | 'classic';

function PracticeTab() {
  const { t } = useTranslation();
  const [mode, setMode] = useState<PracticeMode>('live');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <ModeButton
          active={mode === 'live'}
          onClick={() => setMode('live')}
          icon="graphic_eq"
          label={t('ielts.speaking.live.modeLive')}
        />
        <ModeButton
          active={mode === 'classic'}
          onClick={() => setMode('classic')}
          icon="forum"
          label={t('ielts.speaking.live.modeClassic')}
        />
      </div>

      {mode === 'live' ? <LivePractice /> : <ClassicPractice />}
    </div>
  );
}

function ModeButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: string;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill px-4 py-1.5 text-xs font-semibold uppercase tracking-label transition',
        active
          ? 'bg-primary text-on-primary'
          : 'bg-surface-container-lowest text-on-surface-variant ghost-border hover:bg-surface-container-low',
      )}
    >
      <span className="material-symbols-outlined text-base">{icon}</span>
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Live conversation mode (primary): continuous speech-to-speech with the AI.
// ---------------------------------------------------------------------------

function LivePractice() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [graded, setGraded] = useState<SpeakingSession | null>(null);
  // Приход из тренажёра. Тренажёр УЖЕ подобрал задание по слабому типу,
  // поэтому выбор карточки здесь показывать нельзя: ученик решит, что первый
  // экран был декоративным, и выберет что-то другое — адаптивность пропадёт.
  const fromTrainer = (useLocation().state as { trainerPrompt?: TrainerHandoff } | null)?.trainerPrompt;

  const [inSession, setInSession] = useState(!!fromTrainer);
  const [cueCardId, setCueCardId] = useState<string | null>(fromTrainer?.cue_card_id ?? null);

  const cards = useQuery({
    queryKey: ['ielts-speaking', 'cue-cards'],
    queryFn: () => ieltsSpeakingApi.listCueCards(),
  });

  if (graded) {
    return (
      <div className="space-y-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setGraded(null);
            setInSession(false);
          }}
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          {t('ielts.speaking.backToStart')}
        </Button>
        <ResultCard session={graded} />
      </div>
    );
  }

  if (inSession) {
    return (
      <LiveSpeakingSession
        cueCardId={cueCardId}
        onExit={() => setInSession(false)}
        onGraded={(s) => {
          setGraded(s);
          qc.invalidateQueries({ queryKey: ['ielts-speaking', 'history'] });
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <Card variant="subtle">
        <CardTitle>{t('ielts.speaking.live.setupTitle')}</CardTitle>
        <p className="mt-1 text-sm text-on-surface-variant">{t('ielts.speaking.live.setupHint')}</p>
      </Card>

      <Card>
        <p className="text-xs font-semibold uppercase tracking-label text-on-surface-variant">
          {t('ielts.speaking.pickCueCard')}
        </p>

        {cards.isLoading && (
          <p className="mt-3 text-sm text-on-surface-variant">{t('common.loading')}</p>
        )}
        {cards.isError && <p className="mt-3 text-sm text-error">{humanizeError(cards.error)}</p>}

        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => setCueCardId(null)}
            className={cn(
              'rounded-card p-3 text-left ring-1 ring-inset transition',
              cueCardId === null
                ? 'bg-primary-container ring-primary'
                : 'bg-surface-container-low ring-outline-variant/20 hover:bg-surface-container',
            )}
          >
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-primary">shuffle</span>
              <span className="font-semibold text-on-surface">{t('ielts.speaking.randomCard')}</span>
            </div>
            <p className="mt-1 text-xs text-on-surface-variant">
              {t('ielts.speaking.randomCardHint')}
            </p>
          </button>

          {cards.data?.map((card) => (
            <button
              key={card.id}
              type="button"
              onClick={() => setCueCardId(card.id)}
              className={cn(
                'rounded-card p-3 text-left ring-1 ring-inset transition',
                cueCardId === card.id
                  ? 'bg-primary-container ring-primary'
                  : 'bg-surface-container-low ring-outline-variant/20 hover:bg-surface-container',
              )}
            >
              <p className="font-semibold text-on-surface">{card.title}</p>
              {card.topic && <p className="mt-1 text-xs text-on-surface-variant">{card.topic}</p>}
            </button>
          ))}
        </div>

        <div className="mt-4">
          <Button onClick={() => setInSession(true)}>
            <span className="material-symbols-outlined">graphic_eq</span>
            {t('ielts.speaking.live.continue')}
          </Button>
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Classic (turn-based) mode — kept as a fallback.
// ---------------------------------------------------------------------------

function ClassicPractice() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [session, setSession] = useState<SpeakingSession | null>(null);

  const onFinished = (s: SpeakingSession) => {
    setSession(s);
    qc.invalidateQueries({ queryKey: ['ielts-speaking', 'history'] });
  };

  if (session) {
    if (session.status === 'graded') {
      return (
        <div className="space-y-4">
          <Button variant="ghost" size="sm" onClick={() => setSession(null)}>
            <span className="material-symbols-outlined text-base">arrow_back</span>
            {t('ielts.speaking.backToStart')}
          </Button>
          <ResultCard session={session} />
        </div>
      );
    }
    return (
      <LiveSession
        initial={session}
        onExit={() => setSession(null)}
        onFinished={onFinished}
      />
    );
  }

  return <StartScreen onStarted={setSession} />;
}

function StartScreen({ onStarted }: { onStarted: (s: SpeakingSession) => void }) {
  const { t } = useTranslation();
  const [cueCardId, setCueCardId] = useState<string | null>(null);

  const cards = useQuery({
    queryKey: ['ielts-speaking', 'cue-cards'],
    queryFn: () => ieltsSpeakingApi.listCueCards(),
  });

  const start = useMutation({
    mutationFn: (body?: { cue_card_id?: string }) => ieltsSpeakingApi.startSession(body),
    onSuccess: (r) => onStarted(r),
  });

  const begin = () =>
    start.mutate(cueCardId ? { cue_card_id: cueCardId } : undefined);

  return (
    <div className="space-y-4">
      <Card variant="subtle">
        <CardTitle>{t('ielts.speaking.startTitle')}</CardTitle>
        <p className="mt-1 text-sm text-on-surface-variant">{t('ielts.speaking.startHint')}</p>
      </Card>

      <Card>
        <p className="text-xs font-semibold uppercase tracking-label text-on-surface-variant">
          {t('ielts.speaking.pickCueCard')}
        </p>

        {cards.isLoading && (
          <p className="mt-3 text-sm text-on-surface-variant">{t('common.loading')}</p>
        )}
        {cards.isError && (
          <p className="mt-3 text-sm text-error">{humanizeError(cards.error)}</p>
        )}

        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => setCueCardId(null)}
            className={cn(
              'rounded-card p-3 text-left ring-1 ring-inset transition',
              cueCardId === null
                ? 'bg-primary-container ring-primary'
                : 'bg-surface-container-low ring-outline-variant/20 hover:bg-surface-container',
            )}
          >
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-primary">shuffle</span>
              <span className="font-semibold text-on-surface">{t('ielts.speaking.randomCard')}</span>
            </div>
            <p className="mt-1 text-xs text-on-surface-variant">
              {t('ielts.speaking.randomCardHint')}
            </p>
          </button>

          {cards.data?.map((card) => (
            <button
              key={card.id}
              type="button"
              onClick={() => setCueCardId(card.id)}
              className={cn(
                'rounded-card p-3 text-left ring-1 ring-inset transition',
                cueCardId === card.id
                  ? 'bg-primary-container ring-primary'
                  : 'bg-surface-container-low ring-outline-variant/20 hover:bg-surface-container',
              )}
            >
              <p className="font-semibold text-on-surface">{card.title}</p>
              {card.topic && (
                <p className="mt-1 text-xs text-on-surface-variant">{card.topic}</p>
              )}
            </button>
          ))}
        </div>

        {start.isError && (
          <p className="mt-3 text-sm text-error">{humanizeError(start.error)}</p>
        )}

        <div className="mt-4">
          <Button onClick={begin} loading={start.isPending} disabled={start.isPending}>
            <span className="material-symbols-outlined">play_arrow</span>
            {t('ielts.speaking.begin')}
          </Button>
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Live session: chat turns + mic / text + part indicator + Part 2 prep timer
// ---------------------------------------------------------------------------

function LiveSession({
  initial,
  onExit,
  onFinished,
}: {
  initial: SpeakingSession;
  onExit: () => void;
  onFinished: (s: SpeakingSession) => void;
}) {
  const { t } = useTranslation();
  const [session, setSession] = useState<SpeakingSession>(initial);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);

  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const turns = session.turns ?? [];
  const part = session.part;

  // Auto-scroll the transcript as new turns arrive.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns.length]);

  // Auto-play the most recent examiner turn as speech (voice out) — same logic
  // as AISessionModal. We track the last spoken index so each examiner turn is
  // voiced exactly once.
  const spokenRef = useRef(0);
  useEffect(() => {
    const last = turns[turns.length - 1];
    if (!last || last.role !== 'examiner') return;
    if (turns.length <= spokenRef.current) return;
    spokenRef.current = turns.length;
    let url: string | null = null;
    speakingApi
      .synthesize(last.content)
      .then((blob) => {
        url = URL.createObjectURL(blob);
        playUrl(url);
      })
      .catch(() => {
        // TTS failure is non-blocking.
      });
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [turns]);

  const sendText = async (raw: string) => {
    const text = raw.trim();
    if (!text || busy) return;
    // Optimistic student turn so the chat feels responsive.
    const optimistic: SpeakingTurn = { role: 'student', content: text, part };
    setSession((s) => ({ ...s, turns: [...(s.turns ?? []), optimistic] }));
    setDraft('');
    setBusy(true);
    setError(null);
    try {
      const r = await ieltsSpeakingApi.respond(session.id, { text });
      setSession(r);
    } catch (e) {
      // Roll back the optimistic turn so the student can retry.
      setSession((s) => ({ ...s, turns: (s.turns ?? []).slice(0, -1) }));
      setDraft(text);
      setError(humanizeError(e));
    } finally {
      setBusy(false);
    }
  };

  const send = () => sendText(draft);

  // Voice in: record → Whisper (/ai/transcribe) → respond. Needs a secure
  // context (HTTPS) for microphone access.
  const toggleVoice = async () => {
    if (recording) {
      recRef.current?.stop();
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError(t('ielts.speaking.micHttpsError'));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        setTranscribing(true);
        try {
          const { text } = await speakingApi.transcribe(blob);
          if (text?.trim()) await sendText(text);
          else setError(t('ielts.speaking.transcribeError'));
        } catch (e) {
          setError(humanizeError(e));
        } finally {
          setTranscribing(false);
        }
      };
      recRef.current = rec;
      rec.start();
      setRecording(true);
      setError(null);
    } catch {
      setError(t('ielts.speaking.micAccessError'));
    }
  };

  const finish = useMutation({
    mutationFn: () => ieltsSpeakingApi.finishSession(session.id),
    onSuccess: (s) => onFinished(s),
  });

  const busyAny = busy || transcribing;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <Button variant="ghost" size="sm" onClick={onExit} disabled={finish.isPending}>
          <span className="material-symbols-outlined text-base">arrow_back</span>
          {t('ielts.speaking.exit')}
        </Button>
        <PartIndicator part={part} />
      </div>

      {part === 2 && <CueCardPanel card={session.cue_card ?? null} />}

      <Card className="p-0">
        <div ref={scrollRef} className="max-h-[28rem] space-y-2 overflow-y-auto p-4">
          {turns.length === 0 ? (
            <p className="py-6 text-center text-sm text-on-surface-variant">
              {t('ielts.speaking.examinerThinking')}
            </p>
          ) : (
            turns.map((turn, i) => <TurnBubble key={i} turn={turn} />)
          )}
        </div>

        <div className="border-t border-outline-variant/20 p-3">
          {error && <p className="mb-2 text-sm text-error">{error}</p>}
          <div className="flex gap-2">
            <Button
              variant={recording ? 'primary' : 'ghost'}
              onClick={toggleVoice}
              disabled={busy || transcribing}
              title={recording ? t('ielts.speaking.stopRecord') : t('ielts.speaking.startVoice')}
              className={recording ? 'animate-pulse' : ''}
            >
              <span className="material-symbols-outlined">
                {transcribing ? 'progress_activity' : recording ? 'stop_circle' : 'mic'}
              </span>
            </Button>
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !busyAny && (e.preventDefault(), send())}
              placeholder={
                recording
                  ? t('ielts.speaking.placeholderRecording')
                  : transcribing
                    ? t('ielts.speaking.placeholderTranscribing')
                    : t('ielts.speaking.placeholderDefault')
              }
              className="flex-1 rounded-button bg-surface-container-low px-4 py-2 text-on-surface outline-none ring-1 ring-inset ring-outline-variant/30 focus:ring-2 focus:ring-primary"
              disabled={busyAny || recording}
            />
            <Button disabled={!draft.trim() || busyAny} onClick={send}>
              <span className="material-symbols-outlined">send</span>
            </Button>
          </div>
        </div>
      </Card>

      {finish.isError && (
        <Card variant="subtle" className="text-error">
          {humanizeError(finish.error)}
        </Card>
      )}

      <div className="flex justify-end">
        <Button
          variant="secondary"
          onClick={() => finish.mutate()}
          loading={finish.isPending}
          disabled={finish.isPending || busyAny || turns.length < 2}
        >
          <span className="material-symbols-outlined">grading</span>
          {t('ielts.speaking.finish')}
        </Button>
      </div>
    </div>
  );
}

function TurnBubble({ turn }: { turn: SpeakingTurn }) {
  const { t } = useTranslation();
  const isStudent = turn.role === 'student';
  return (
    <div className={cn('flex', isStudent ? 'justify-end' : 'justify-start')}>
      <div className={cn('max-w-[80%]', isStudent ? 'text-right' : 'text-left')}>
        <span className="text-[10px] font-semibold uppercase tracking-label text-on-surface-variant/70">
          {isStudent ? t('ielts.speaking.you') : t('ielts.speaking.examiner')}
        </span>
        <div
          className={cn(
            'mt-0.5 rounded-card px-3 py-2 text-sm',
            isStudent
              ? 'bg-primary-container text-on-primary-container'
              : 'bg-surface-container-low text-on-surface',
          )}
        >
          {turn.content}
        </div>
      </div>
    </div>
  );
}

function PartIndicator({ part }: { part: SpeakingPart }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1.5">
      {([1, 2, 3] as SpeakingPart[]).map((p) => (
        <span
          key={p}
          className={cn(
            'rounded-pill px-2.5 py-1 text-[11px] font-semibold uppercase tracking-label transition',
            p === part
              ? 'bg-primary text-on-primary'
              : p < part
                ? 'bg-tertiary-container text-on-surface-variant'
                : 'bg-surface-container-low text-on-surface-variant/60',
          )}
        >
          {t('ielts.speaking.partLabel', { n: p })}
        </span>
      ))}
    </div>
  );
}

function CueCardPanel({ card }: { card: SpeakingCueCard | null }) {
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState(PREP_SECONDS);
  const [prepDone, setPrepDone] = useState(false);

  // 1-minute Part 2 prep timer. Runs once on mount; the student may skip.
  useEffect(() => {
    if (prepDone) return;
    if (remaining <= 0) {
      setPrepDone(true);
      return;
    }
    const id = window.setTimeout(() => setRemaining((r) => r - 1), 1000);
    return () => window.clearTimeout(id);
  }, [remaining, prepDone]);

  const mm = Math.floor(remaining / 60);
  const ss = String(remaining % 60).padStart(2, '0');

  return (
    <Card>
      <div className="flex items-center justify-between gap-3">
        <CardTitle>{card?.title ?? t('ielts.speaking.cueCardTitle')}</CardTitle>
        {!prepDone && (
          <div className="flex items-center gap-2">
            <Pill variant="warning">
              <span className="material-symbols-outlined text-sm">timer</span>
              {mm}:{ss}
            </Pill>
            <Button variant="ghost" size="sm" onClick={() => setPrepDone(true)}>
              {t('ielts.speaking.skipPrep')}
            </Button>
          </div>
        )}
      </div>
      {card?.cue_text && (
        <p className="mt-2 whitespace-pre-wrap text-sm text-on-surface-variant">
          {card.cue_text}
        </p>
      )}
      <p className="mt-3 text-xs text-on-surface-variant">
        {prepDone ? t('ielts.speaking.prepOver') : t('ielts.speaking.prepHint')}
      </p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Result card: overall band + criteria bars + tips + transcript
// ---------------------------------------------------------------------------

function ResultCard({ session }: { session: SpeakingSession }) {
  const { t } = useTranslation();
  const bands: Record<SpeakingCriterionKey, number | undefined> = {
    fc: session.band_fc,
    lr: session.band_lr,
    gra: session.band_gra,
    pr: session.band_pr,
  };
  const fb = session.feedback;

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center">
          <OverallBand band={session.band_overall} />
          <div className="flex-1">
            <CardTitle>{t('ielts.speaking.yourBand')}</CardTitle>
            {fb?.summary && (
              <p className="mt-2 text-sm text-on-surface-variant">{fb.summary}</p>
            )}
          </div>
        </div>

        <div className="mt-6 space-y-4">
          {CRITERIA.map((c) => (
            <CriterionBar
              key={c}
              label={t(`ielts.speaking.criterion.${c}`)}
              band={bands[c]}
              comment={fb?.criteria?.[c]}
            />
          ))}
        </div>
      </Card>

      {fb?.tips && fb.tips.length > 0 && (
        <Card>
          <CardTitle>{t('ielts.speaking.tipsTitle')}</CardTitle>
          <ul className="mt-3 space-y-2">
            {fb.tips.map((tip, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-on-surface-variant">
                <span className="material-symbols-outlined mt-0.5 text-base text-warning">
                  lightbulb
                </span>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <TranscriptCard turns={session.turns ?? []} />
    </div>
  );
}

function TranscriptCard({ turns }: { turns: SpeakingTurn[] }) {
  const { t } = useTranslation();
  if (turns.length === 0) return null;
  return (
    <Card>
      <CardTitle>{t('ielts.speaking.transcriptTitle')}</CardTitle>
      <div className="mt-3 space-y-2">
        {turns.map((turn, i) => (
          <TurnBubble key={i} turn={turn} />
        ))}
      </div>
    </Card>
  );
}

function OverallBand({ band }: { band?: number }) {
  const { t } = useTranslation();
  const frac = bandFraction(band);
  const deg = Math.round(frac * 360);
  return (
    <div className="flex flex-col items-center">
      <div
        className="grid h-32 w-32 place-items-center rounded-full"
        style={{
          background: `conic-gradient(#FF941F ${deg}deg, rgba(127,127,127,0.18) ${deg}deg)`,
        }}
      >
        <div className="grid h-24 w-24 place-items-center rounded-full bg-surface-container-lowest">
          <span className="font-display text-4xl font-extrabold text-on-surface">
            {typeof band === 'number' ? band.toFixed(1) : '—'}
          </span>
        </div>
      </div>
      <span className="mt-2 text-xs font-semibold uppercase tracking-label text-on-surface-variant">
        {t('ielts.speaking.overall')}
      </span>
    </div>
  );
}

function CriterionBar({
  label,
  band,
  comment,
}: {
  label: string;
  band?: number;
  comment?: string;
}) {
  const pct = Math.round(bandFraction(band) * 100);
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between">
        <span className="text-sm font-semibold text-on-surface">{label}</span>
        <span className="text-sm font-bold text-primary">
          {typeof band === 'number' ? band.toFixed(1) : '—'}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-pill bg-surface-container-low">
        <div className="h-full rounded-pill bg-primary transition-all" style={{ width: `${pct}%` }} />
      </div>
      {comment && <p className="mt-1 text-xs text-on-surface-variant">{comment}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// History tab: list + band trend chart
// ---------------------------------------------------------------------------

function HistoryTab() {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<string | null>(null);

  const history = useQuery({
    queryKey: ['ielts-speaking', 'history'],
    queryFn: () => ieltsSpeakingApi.listSessions(),
  });

  if (history.isLoading) return <Card variant="subtle">{t('common.loading')}</Card>;
  if (history.isError) {
    return (
      <Card variant="subtle" className="text-error">
        {humanizeError(history.error)}
      </Card>
    );
  }
  if (!history.data?.length) {
    return (
      <Card variant="subtle" className="text-center text-on-surface-variant">
        <span className="material-symbols-outlined mb-2 block text-4xl text-on-surface-variant/60">
          record_voice_over
        </span>
        {t('ielts.speaking.noHistory')}
      </Card>
    );
  }

  // Band trend over graded sessions, oldest → newest (history is newest-first).
  const trend = history.data
    .filter((s) => s.status === 'graded' && typeof s.band_overall === 'number')
    .map((s) => ({ date: s.created_at, band: s.band_overall as number }))
    .reverse();

  return (
    <div className="space-y-4">
      {trend.length > 1 && <BandTrendChart points={trend} />}

      <ul className="space-y-2">
        {history.data.map((s) => (
          <li key={s.id}>
            <HistoryRow
              session={s}
              expanded={openId === s.id}
              onToggle={() => setOpenId(openId === s.id ? null : s.id)}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

function BandTrendChart({ points }: { points: { date: string; band: number }[] }) {
  const { t } = useTranslation();
  const { W, H, PAD, path, dots } = useMemo(() => {
    const w = 520;
    const h = 160;
    const pad = 24;
    const n = points.length;
    const max = 9;
    const x = (i: number) => pad + (i * (w - pad * 2)) / Math.max(1, n - 1);
    const y = (b: number) => h - pad - (b / max) * (h - pad * 2);
    const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(p.band)}`).join(' ');
    const ds = points.map((p, i) => ({ cx: x(i), cy: y(p.band), band: p.band }));
    return { W: w, H: h, PAD: pad, path: d, dots: ds };
  }, [points]);

  return (
    <Card>
      <CardTitle>{t('ielts.speaking.bandTrend')}</CardTitle>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="mt-3 h-40 w-full"
        preserveAspectRatio="none"
        role="img"
        aria-label={t('ielts.speaking.bandTrend')}
      >
        <line
          x1={PAD}
          y1={H - PAD}
          x2={W - PAD}
          y2={H - PAD}
          stroke="currentColor"
          strokeOpacity={0.15}
        />
        <path d={path} fill="none" stroke="#FF941F" strokeWidth={2.5} />
        {dots.map((d, i) => (
          <g key={i}>
            <circle cx={d.cx} cy={d.cy} r={4} fill="#FF941F" />
            <text
              x={d.cx}
              y={d.cy - 8}
              textAnchor="middle"
              className="fill-current text-[10px]"
            >
              {d.band.toFixed(1)}
            </text>
          </g>
        ))}
      </svg>
    </Card>
  );
}

function HistoryRow({
  session,
  expanded,
  onToggle,
}: {
  session: SpeakingSession;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const detail = useQuery({
    queryKey: ['ielts-speaking', 'session', session.id],
    queryFn: () => ieltsSpeakingApi.getSession(session.id),
    enabled: expanded,
  });

  const date = session.created_at ? new Date(session.created_at).toLocaleDateString() : '';

  return (
    <Card variant="subtle" className="overflow-hidden p-0">
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-3 p-4 text-left transition hover:bg-surface-container-low"
      >
        <span className="material-symbols-outlined text-primary">record_voice_over</span>
        <div className="flex-1">
          <p className="font-semibold text-on-surface">{t('ielts.speaking.mockSession')}</p>
          <p className="text-xs text-on-surface-variant">{date}</p>
        </div>
        {session.status === 'graded' && typeof session.band_overall === 'number' ? (
          <Pill variant="primary">{session.band_overall.toFixed(1)}</Pill>
        ) : session.status === 'active' ? (
          <Pill variant="neutral">{t('ielts.speaking.statusActive')}</Pill>
        ) : (
          <Pill variant="danger">{t('ielts.speaking.statusFailed')}</Pill>
        )}
        <span className="material-symbols-outlined text-on-surface-variant">
          {expanded ? 'expand_less' : 'expand_more'}
        </span>
      </button>

      {expanded && (
        <div className="border-t border-outline-variant/20 p-4">
          {detail.isLoading && (
            <p className="text-sm text-on-surface-variant">{t('common.loading')}</p>
          )}
          {detail.isError && <p className="text-sm text-error">{humanizeError(detail.error)}</p>}
          {detail.data && detail.data.status === 'graded' && (
            <ResultCard session={detail.data} />
          )}
          {detail.data && detail.data.status === 'failed' && (
            <p className="text-sm text-error">{t('ielts.speaking.gradingFailed')}</p>
          )}
          {detail.data && detail.data.status === 'active' && (
            <p className="text-sm text-on-surface-variant">{t('ielts.speaking.sessionUnfinished')}</p>
          )}
        </div>
      )}
    </Card>
  );
}
